import { useTranslation } from 'react-i18next';
import { Route, Switch } from 'react-router-dom';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import { GiftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { PageType } from '@treams/treams-network';
import {
	CardList,
	Container,
	FlexGap,
	Jumbo,
	PAGE_CONTENT_WIDTH,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { ComplimentCreateModalWithID } from '../packages/kakapo-ui-application/pages/compliments/ComplimentCreateModal';
import ComplimentCreateThankYou from '../packages/kakapo-ui-application/pages/compliments/ComplimentThankYou';
import IncomingComplimentCard from './IncomingComplimentCard';
import OutgoingComplimentCard from './OutgoingComplimentCard';
import ViewerFeedbackButtons from './ViewerFeedbackButtons';
import ViewerFeedbackTabs from './ViewerFeedbackTabs';

export default function ViewerCompliments() {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();
	const userID = viewer.id;

	const query = graphql.useComplimentsQuery({
		variables: { last: 10, id: userID },
	});

	const givenQuery = graphql.useGivenComplimentsQuery({
		variables: { last: 10 },
	});

	return (
		<>
			<Jumbo.Editable
				hasMediaUpload
				page={PageType.Compliments}
				title={t('feedback.compliments.title')}
				description={t('feedback.compliments.description')}
				button={<ViewerFeedbackButtons />}
				footer={<ViewerFeedbackTabs />}
			/>

			<Container mt="48px" mb={4} wide={PAGE_CONTENT_WIDTH}>
				<FlexGap flexDirection="column" gap={tokens.gap.page}>
					<FlexGap flexDirection="column" gap={tokens.gap.section}>
						<Subheading>{t('feedback.compliments.section.incoming')}</Subheading>
						<CardList
							translationName="compliments.incoming"
							query={query}
							path="user.compliments"
							icon={<GiftIcon />}
							compact
							renderCard={(item: graphql.Compliment) => (
								<IncomingComplimentCard key={item.id} userID={userID} compliment={item} />
							)}
							renderSkeleton={() => <IncomingComplimentCard.Skeleton />}
						/>
					</FlexGap>

					<FlexGap flexDirection="column" gap={tokens.gap.section}>
						<Subheading>{t('feedback.compliments.section.outgoing')}</Subheading>
						<CardList
							translationName="compliments.outgoing"
							query={givenQuery}
							path="viewer.givenCompliments"
							icon={<GiftIcon />}
							compact
							renderCard={(item: graphql.Compliment) => (
								<OutgoingComplimentCard key={item.id} compliment={item} />
							)}
							renderSkeleton={() => <OutgoingComplimentCard.Skeleton />}
						/>
					</FlexGap>
				</FlexGap>
			</Container>

			<Switch>
				<Route path="/users/:id/feedback/compliments/new">
					{({ match }) => <ComplimentCreateModalWithID userID={match!.params.id!} />}
				</Route>
				<Route path="/users/:id/feedback/compliments/thank-you">
					{({ match }) => <ComplimentCreateThankYou userID={match!.params.id!} />}
				</Route>
			</Switch>
		</>
	);
}
